import { Component } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    this.setState({ info });
    console.error("ErrorBoundary capturé:", error, info);
  }
  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, info } = this.state;
    const msg = error?.message || String(error);
    const stack = error?.stack || "";
    const componentStack = info?.componentStack || "";

    return (
      <div style={{ minHeight: "100vh", background: "#FAF9F6", fontFamily: "Manrope, sans-serif", padding: "2rem", boxSizing: "border-box" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>

          {/* Header */}
          <div style={{ background: "#fff", border: "1px solid #f87171", borderRadius: "12px", padding: "1.5rem 2rem", marginBottom: "1.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
              <span style={{ background: "#fee2e2", color: "#dc2626", borderRadius: "6px", padding: "0.2rem 0.6rem", fontSize: "0.7rem", fontWeight: "600", letterSpacing: "0.05em", textTransform: "uppercase" }}>Erreur React</span>
            </div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#2A332C", fontSize: "1.4rem", margin: "0 0 0.5rem" }}>
              Une erreur est survenue
            </h2>
            <p style={{ color: "#dc2626", fontWeight: "500", fontSize: "0.9rem", margin: 0, fontFamily: "monospace", wordBreak: "break-word" }}>
              {msg}
            </p>
          </div>

          {/* Causes probables */}
          <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "12px", padding: "1.25rem 1.5rem", marginBottom: "1.5rem" }}>
            <p style={{ fontWeight: "600", color: "#92400e", fontSize: "0.85rem", margin: "0 0 0.75rem" }}>Causes probables sur IONOS :</p>
            <ul style={{ margin: 0, paddingLeft: "1.25rem", color: "#78350f", fontSize: "0.82rem", lineHeight: "1.8" }}>
              <li><strong>REACT_APP_BACKEND_URL manquant</strong> au moment du build → URL = "undefined/api/..."</li>
              <li><strong>Backend inaccessible</strong> (FastAPI non démarré ou mauvaise URL)</li>
              <li><strong>CORS bloqué</strong> → le backend refuse les requêtes depuis ce domaine</li>
              <li><strong>Dépendance manquante</strong> → vérifier que le build inclut tous les packages</li>
            </ul>
          </div>

          {/* Message d'erreur complet */}
          {stack && (
            <div style={{ marginBottom: "1.5rem" }}>
              <p style={{ fontWeight: "600", color: "#5A6E60", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>Stack trace</p>
              <pre style={{ background: "#1e1e1e", color: "#f8f8f2", padding: "1rem", borderRadius: "10px", fontSize: "0.72rem", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: "1.6", margin: 0 }}>
                {stack}
              </pre>
            </div>
          )}

          {/* Composant React */}
          {componentStack && (
            <div style={{ marginBottom: "1.5rem" }}>
              <p style={{ fontWeight: "600", color: "#5A6E60", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>Composant React concerné</p>
              <pre style={{ background: "#1e1e1e", color: "#a5f3fc", padding: "1rem", borderRadius: "10px", fontSize: "0.72rem", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: "1.6", margin: 0 }}>
                {componentStack.trim()}
              </pre>
            </div>
          )}

          {/* Infos environnement */}
          <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "12px", padding: "1.25rem 1.5rem", marginBottom: "1.5rem" }}>
            <p style={{ fontWeight: "600", color: "#166534", fontSize: "0.85rem", margin: "0 0 0.75rem" }}>Informations environnement</p>
            <table style={{ width: "100%", fontSize: "0.8rem", borderCollapse: "collapse" }}>
              {[
                ["URL Backend (build)", process.env.REACT_APP_BACKEND_URL || "⚠️ undefined — variable manquante au build !"],
                ["URL courante", window.location.href],
                ["User Agent", navigator.userAgent.substring(0, 80) + "..."],
                ["Date", new Date().toLocaleString("fr-FR")],
              ].map(([k, v]) => (
                <tr key={k} style={{ borderBottom: "1px solid #bbf7d0" }}>
                  <td style={{ padding: "0.4rem 0.75rem 0.4rem 0", fontWeight: "600", color: "#166534", whiteSpace: "nowrap", verticalAlign: "top" }}>{k}</td>
                  <td style={{ padding: "0.4rem 0", color: "#15803d", fontFamily: "monospace", wordBreak: "break-all" }}>{v}</td>
                </tr>
              ))}
            </table>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <button onClick={() => window.location.reload()}
              style={{ background: "#7C9082", color: "white", border: "none", borderRadius: "9999px", padding: "0.65rem 1.5rem", cursor: "pointer", fontFamily: "Manrope, sans-serif", fontSize: "0.85rem", fontWeight: "500" }}>
              Rafraîchir la page
            </button>
            <button onClick={() => { this.setState({ hasError: false, error: null, info: null }); window.location.href = "/"; }}
              style={{ background: "white", color: "#7C9082", border: "1.5px solid #7C9082", borderRadius: "9999px", padding: "0.65rem 1.5rem", cursor: "pointer", fontFamily: "Manrope, sans-serif", fontSize: "0.85rem", fontWeight: "500" }}>
              Retour à l'accueil
            </button>
            <button onClick={() => { navigator.clipboard?.writeText(`ERREUR: ${msg}\n\nSTACK:\n${stack}\n\nCOMPOSANT:\n${componentStack}`); alert("Erreur copiée dans le presse-papier"); }}
              style={{ background: "white", color: "#5A6E60", border: "1.5px solid #d1d5db", borderRadius: "9999px", padding: "0.65rem 1.5rem", cursor: "pointer", fontFamily: "Manrope, sans-serif", fontSize: "0.85rem", fontWeight: "500" }}>
              Copier l'erreur
            </button>
          </div>
        </div>
      </div>
    );
  }
}
